import { Box } from '@mantine/core'
import { groupBy } from '@markku/utils'
import { type PointerEvent, useRef } from 'react'
import {
  KeepScale,
  type ReactZoomPanPinchContentRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'
import { type FloorPlanMarker, Marker, MARKER_SIZE } from './Marker'
import { useTap } from './useTap'

export type { FloorPlanMarker } from './Marker'

interface FloorPlanViewProps {
  src: string
  markers: FloorPlanMarker[]
  height: number | string
  /** Turns a tap on the plan into a placement, as fractions of the image. */
  onPlace?: (point: { x: number; y: number }) => void
  onMarkerClick?: (key: string) => void
}

/**
 * A floor plan image with numbered markers, pinch-zoomable. Markers keep their
 * on-screen size at any zoom so a 4× view doesn't bury the drawing under them.
 * Give it a key per plan: it centres once, on the first load.
 */
export function FloorPlanView({
  src,
  markers,
  height,
  onPlace,
  onMarkerClick,
}: FloorPlanViewProps) {
  const transformRef = useRef<ReactZoomPanPinchContentRef>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const centeredRef = useRef(false)

  const tap = useTap((e: PointerEvent) => {
    const key = (e.target as HTMLElement).closest<HTMLElement>(
      '[data-marker-key]',
    )?.dataset.markerKey
    if (!onPlace) {
      if (key) onMarkerClick?.(key)
      return
    }

    // A tap on a marker means "the same spot": an after photo lands exactly
    // on its before photo, so the two share one number.
    const marker = markers.find((m) => String(m.key) === key)
    if (marker) {
      onPlace({ x: marker.x, y: marker.y })
      return
    }

    const rect = (imageRef.current as HTMLImageElement).getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    if (x < 0 || x > 1 || y < 0 || y > 1) return
    onPlace({ x, y })
  })

  // Markers on the same spot (a valve's numbered spot and an upsell photo of it)
  // sit side by side instead of hiding each other.
  const offsets = new Map(
    [...groupBy(markers, (m) => `${m.x},${m.y}`).values()].flatMap((group) =>
      group.map((m, i) => [
        m.key,
        (i - (group.length - 1) / 2) * (MARKER_SIZE + 4),
      ]),
    ),
  )

  // The plan arrives after the wrapper measured an empty box. Only the first
  // load centres: a refetch swaps in a freshly signed URL for the same plan,
  // and recentring then would throw away the worker's zoom.
  const centreOnFirstLoad = () => {
    if (centeredRef.current) return
    centeredRef.current = true
    transformRef.current?.centerView(1, 0)
  }

  return (
    <Box h={height} bg="gray.1" style={{ borderRadius: 8, overflow: 'hidden' }}>
      <TransformWrapper
        ref={transformRef}
        maxScale={8}
        centerOnInit
        // Placing wants every tap; a double tap would drop a pin and zoom.
        doubleClick={{ disabled: !!onPlace }}
      >
        <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
          <Box
            pos="relative"
            {...tap}
            style={{ cursor: onPlace ? 'crosshair' : undefined }}
          >
            <img
              ref={imageRef}
              src={src}
              alt=""
              draggable={false}
              onLoad={centreOnFirstLoad}
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: height,
                userSelect: 'none',
              }}
            />
            {markers.map((marker) => (
              <Box
                key={marker.key}
                pos="absolute"
                left={`${marker.x * 100}%`}
                top={`${marker.y * 100}%`}
              >
                <KeepScale
                  data-marker-key={marker.key}
                  style={{
                    position: 'absolute',
                    left: -MARKER_SIZE / 2,
                    top: -MARKER_SIZE / 2,
                    width: MARKER_SIZE,
                    height: MARKER_SIZE,
                    cursor: onMarkerClick && !onPlace ? 'pointer' : undefined,
                  }}
                >
                  <Marker marker={marker} offset={offsets.get(marker.key)} />
                </KeepScale>
              </Box>
            ))}
          </Box>
        </TransformComponent>
      </TransformWrapper>
    </Box>
  )
}
